import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as searchActions from '../../actions/searchActions';
import { addQuery } from '../../utils/routerUtils';
import PageContainer from '../Global/Containers/PageContainer';
import MaxWidthContainer from '../Global/Containers/MaxWidthContainer';
import MainContentContainer from '../Global/Containers/MainContentContainer';
import SearchHeading from './SearchHeading';
import SpellCheck from './SpellCheck';
import SearchPrimaryFilters from './SearchPrimaryFilters';
import SearchTypeTopResults from './SearchTypeTopResults';
import SearchList from './SearchList';
import SearchSecondaryFilters from './SearchSecondaryFilters';
import NoResultsMessage from '../Global/Messages/NoResultsMessage';
import Pager from '../Global/Pager/Pager';
import PageNumberInfo from '../Global/Pager/PageNumberInfo';

class SearchPage extends Component {
  constructor(props) {
    super(props);

    const { term, primaryFilter, pageNum, spellcheckEnabled, secondaryFilters, sortBy } = props.location.query;

    //  URL encoding of secondaryFilters
    //  __ to join two filters e.g. has_video__has_animation
    //  .  to join filter name to value e.g. phase.Attacking
    //  -  to join multi value filters (checkboxes) e.g. theme.Dribbling-Possession
    //     prefix multi value filters with '~' to distinguish from others with format filter.value
    //      e.g. theme.~Dribbling-Possession
    this.secondaryFilterSeparator = `__`;
    this.secondaryFilterValueSeparator = `.`;
    this.secondaryFilterMultiValueSeparator = `-`;
    this.secondaryFilterMultiValuePrefix = `~`;

    // URL encode sortBy
    this.sortByURLEncodeParams = [`:`, `~`];

    this.state = {
      // Our own object for setting the URL
      searchQuery: {
        term: term || ``,
        primaryFilter: primaryFilter || `all`,
        pageNum: pageNum || 1,
        spellcheckEnabled: this.parseSpellcheckQueryParam(spellcheckEnabled),
        secondaryFilters: secondaryFilters || ``,
        sortBy: sortBy || ``,
      },
      filterDropdownOpen: false,
      loadingSearchResults: true,
      // This object should mirror what the API is expecting
      activeSecondaryFilters: this.parseSecondaryFiltersParamsToObj(secondaryFilters, sortBy),
    };
  }

  componentDidMount() {
    // Initial API call based off of data in the URL
    this.getSearchResults({});
    // Get all secondary filters
    this.props.actions.getSecondaryFilters(`all`);
  }

  componentWillReceiveProps(nextProps) {
    // Once the Search API has fired and returned results
    if (this.props.search.results !== nextProps.search.results) {
      const term = this.setSearchTerm(nextProps.search.original_query);
      const spellcheckEnabled = this.parseSpellcheckQueryParam(nextProps.location.query.spellcheckEnabled);
      const newSearchQuery = { ...this.state.searchQuery, ...nextProps.location.query, term, spellcheckEnabled };

      this.setState({
        loadingSearchResults: false,
        searchQuery: newSearchQuery,
      });
    }
  }

  // We need this function as the spellcheckEnabled state property should be a boolean, but when obtained via a URL query param
  // it is returned as string, so we need to parse it to be a boolean
  parseSpellcheckQueryParam = (isSpellcheckActive) => {
    switch (typeof isSpellcheckActive) {
      // If it is a query param, do said parsing to make it a boolean
      case `string`:
        return JSON.parse(isSpellcheckActive);
      // If it isn't included as a URL query param
      case `undefined`:
        return true;
      default:
        return isSpellcheckActive;
    }
  };

  secondaryParamToFilter = (param) => {
    const filter = {};
    const parts = param.split(this.secondaryFilterValueSeparator);
    if (parts.length === 1) {
      // simple filter
      filter[param] = true;
    } else if (parts[1].startsWith(this.secondaryFilterMultiValuePrefix)) {
      // checkbox filter
      filter[parts[0]] = parts[1].slice(1).split(this.secondaryFilterMultiValueSeparator);
    } else {
      // everything else e.g. radio
      filter[parts[0]] = parts[1];
    }
    return filter;
  };

  sortByParamToValue = (param) => {
    const reverseEncoding = [...this.sortByURLEncodeParams].reverse();
    return param.replace(...reverseEncoding);
  };

  parseSecondaryFiltersParamsToObj = (secondaryFiltersParam, sortBy) => {
    const activeSecondaryFilters = {};
    if (typeof secondaryFiltersParam === `string` && secondaryFiltersParam.length > 0) {
      secondaryFiltersParam.split(this.secondaryFilterSeparator).forEach((param) => {
        Object.assign(activeSecondaryFilters, this.secondaryParamToFilter(param));
      });
    }
    if (typeof sortBy === `string` && sortBy.length > 0) {
      // ToDo: sortBy shouldn't be considered a filter, separate it out
      activeSecondaryFilters.sort_by = this.sortByParamToValue(sortBy);
    }
    return activeSecondaryFilters;
  };

  secondaryFilterToParam = (filter, value) => {
    if (typeof value === `boolean`) {
      // simple filter
      return filter;
    } else if (Array.isArray(value)) {
      // checkbox filter
      // prefix list of items with '~' to distinguish list of one from default filters below
      return `${filter}${this.secondaryFilterValueSeparator}${this.secondaryFilterMultiValuePrefix}${value.join(this.secondaryFilterMultiValueSeparator)}`;
    }
    // everything else e.g. radio buttons
    return `${filter}${this.secondaryFilterValueSeparator}${value}`;
  };

  sortByValueToParam = (value) => {
    return value.replace(...this.sortByURLEncodeParams);
  };

  parseSecondaryFiltersObjToParams = (secondaryFiltersObj) => {
    const params = [];
    let sortBy = ``;
    if (typeof secondaryFiltersObj === `object`) {
      for (const [key, value] of Object.entries(secondaryFiltersObj)) {
        if (key === `sort_by`) {
          // ToDo: sortBy shouldn't be considered a filter, separate it out
          sortBy = this.sortByValueToParam(value);
        } else {
          params.push(this.secondaryFilterToParam(key, value));
        }
      }
    }
    return [params.join(this.secondaryFilterSeparator), sortBy];
  };

  setSearchTerm = (searchTerm) => {
    let term = this.state.searchQuery.term;
    // If a search term is actually set (not doing a global search with no term in the search bar)
    if (searchTerm !== undefined) {
      // If the search has auto-corrected the initial search
      term = searchTerm;
    }
    return term;
  };

  getSearchResults = ({
    term = this.state.searchQuery.term,
    spellcheckEnabled = this.state.searchQuery.spellcheckEnabled, 
    pageNum = this.state.searchQuery.pageNum,
    primaryFilter = this.state.searchQuery.primaryFilter,
    secondaryFilters = this.state.activeSecondaryFilters,
  }) => {
    // When getting search results we want to clear the current state to display loaders
    this.setState({ loadingSearchResults: true }, () => {
      this.props.actions.searchQuery(term, spellcheckEnabled, pageNum, primaryFilter, secondaryFilters);
    });
  };

  getSecondaryFilters = (primaryFilter) => {
    this.props.actions.getSecondaryFilters(primaryFilter);
  };

  getPageNumFromLink = (link) => {
    const url = new URL(link);
    let pageNum = 1;
    // https://caniuse.com/#feat=urlsearchparams
    // Safari 10 and below don't support searchParams so need this check to prevent the page breaking
    if (url.searchParams) {
      if (url.searchParams.pageNum) {
        pageNum = url.searchParams.get(`pageNum`);
      } else if (url.searchParams.get('page-number')) {
        pageNum = url.searchParams.get(`page-number`);
      }
    }
    return pageNum;
  };

  buildSearchResults = (filter, limit, listItemClasses) => (
    <SearchTypeTopResults key={filter.itemName} filter={filter} onViewAllClick={this.handlePrimaryFilterClick}>
      <SearchList filterName={filter.itemName} searchResults={this.props.search.results} limit={limit} listItemClasses={listItemClasses} />
    </SearchTypeTopResults>
  );

  handleRequestContentClick = () => {
    console.log(`request content!`);
  };

  handleEnableSpellcheckClick = () => {
    this.handleSpellcheckToggle(true);
  };

  handleOriginalQuerySearchClick = () => {
    this.handleSpellcheckToggle(false);
  };

  handleSpellcheckToggle = (isSpellcheckActive) => {
    const newSearchQuery = { ...this.state.searchQuery };
    newSearchQuery.spellcheckEnabled = isSpellcheckActive;
    newSearchQuery.pageNum = 1;
    
    this.setState({ searchQuery: newSearchQuery }, () => {
      addQuery(newSearchQuery);
      this.getSearchResults({ spellcheckEnabled: newSearchQuery.spellcheckEnabled, pageNum: newSearchQuery.pageNum });
    });
  };

  handleDropdownClick = (isDropdownActive) => {
    this.setState({ filterDropdownOpen: isDropdownActive });
  };

  handlePrimaryFilterClick = (filterName) => {
    const newSearchQuery = { ...this.state.searchQuery };
    const newSecondaryFilters = this.updateActiveSecondaryFilters(filterName);
    const newSecondaryFilterParams = this.parseSecondaryFiltersObjToParams(newSecondaryFilters);
    newSearchQuery.primaryFilter = filterName;
    newSearchQuery.pageNum = 1;
    newSearchQuery.secondaryFilters = newSecondaryFilterParams[0];
    newSearchQuery.sortBy = newSecondaryFilterParams[1];

    this.setState({ 
      searchQuery: newSearchQuery, 
      activeSecondaryFilters: newSecondaryFilters,
    }, () => {
      addQuery(newSearchQuery);
      this.getSearchResults({ primaryFilter: newSearchQuery.primaryFilter });
    });
  };

  updateActiveSecondaryFilters = (primaryFilter) => {
    // Whenever we change the primary filter we need to check to see what secondary filters are allowed on that type
    // Depending on the result we need to alter our active secondary filters to prevent passing unsupported filters to the API
    // E.g. 'phase of the game' is only supported by practices, so if this is active but then we switch the primary filter to 'guides'
    // which doesn't support 'phase_of_the_game' we need to remove it from the active secondary filters so that we don't pass
    // it to the search query API as it will return zero results
    const newActiveSecondaryFilters = {};
    const secondaryFilters = this.props.search.secondaryFilters;

    // If the primary filter doesn't support secondary filters (e.g. all, clubs, teams, users etc) then just return an empty object
    if (primaryFilter in secondaryFilters) {
      const allowedFilters = this.props.search.secondaryFilters[primaryFilter];
      
      Object.keys(allowedFilters).forEach((key) => {
        const filter = allowedFilters[key];

        switch (filter.expected_type) {
          // If a string or an array then we simply check to see if the key name exists
          case `string`:
          case `array`:
            if (filter.param in this.state.activeSecondaryFilters) {
              newActiveSecondaryFilters[filter.param] = this.state.activeSecondaryFilters[filter.param];
            }
            break;

          // If there is no expected_type, then we need to loop through the filter options and see if the key exists
          // Used for Age and Video filters
          default:
            filter.options.forEach((f) => {
              if (f.param in this.state.activeSecondaryFilters) {
                newActiveSecondaryFilters[f.param] = this.state.activeSecondaryFilters[f.param];
              }
            });
        }
      });
    }

    return newActiveSecondaryFilters;
  };

  handlePageChangeClick = (value) => {
    const newSearchQuery = { ...this.state.searchQuery };
    newSearchQuery.pageNum = Number(newSearchQuery.pageNum) + value;

    this.setState({ searchQuery: newSearchQuery }, () => {
      addQuery(newSearchQuery);
      this.getSearchResults({ pageNum: newSearchQuery.pageNum });
    });
  };

  handlePrevClick = () => {
    this.handlePageChangeClick(-1);
  };

  handleNextClick = () => {
    this.handlePageChangeClick(1);
  };

  getActiveSecondaryFilters = (target, expectedType) => {
    const { name, value } = target;
    const activeSecondaryFilters = { ...this.state.activeSecondaryFilters };

    switch (expectedType) {
      // If the filter is a radio, we simply override the value
      // e.g. phase_of_the_game: "Defending" becomes phase_of_the_game: "Attacking"
      case `string`:
        // We allow for unchecking or radio buttons. If the clicked filter is already being filtered
        // then delete it from the object
        if (activeSecondaryFilters[name] === value) {
          delete activeSecondaryFilters[name];
        } else {
          activeSecondaryFilters[name] = value;
        }
        break;

      // If it's a checkbox then we need to check whether we are checking or unchecking the value
      case `array`:
        // If the clicked filter is already being filtered
        if (name in activeSecondaryFilters) {
          // If we're unchecking the checkbox
          if (activeSecondaryFilters[name].includes(value)) {
            activeSecondaryFilters[name] = activeSecondaryFilters[name].filter(val => val !== value);
            // If nothing is in the array then delete the key from the object as anything in the object should be a filter
            if (activeSecondaryFilters[name].length === 0) {
              delete activeSecondaryFilters[name];
            }
          // If we're checking the checkbox
          } else {
            activeSecondaryFilters[name] = [...activeSecondaryFilters[name], value];
          }
        } else {
          activeSecondaryFilters[name] = [value];
        }
        break;

      // If there is no expected_type, then we need to treat every object as it's own filter
      // Used for Age and Video filters
      default:
        // If we're unchecking the checkbox we want to delete it from the object, not just set it to false
        // This way we know that everything in our object is an active filter and don't have to check true/false
        if (activeSecondaryFilters[name]) {
          delete activeSecondaryFilters[name];
        // If we're checking the checkbox
        } else {
          activeSecondaryFilters[name] = true;
        }
    }

    return activeSecondaryFilters;
  }

  handleSecondaryFilterChange = (e, expectedType) => {
    const target = e.target;
    const newSearchQuery = { ...this.state.searchQuery };
    const newSecondaryFilters = this.getActiveSecondaryFilters(target, expectedType);
    const newSecondaryFilterParams = this.parseSecondaryFiltersObjToParams(newSecondaryFilters);
    newSearchQuery.pageNum = 1;
    newSearchQuery.secondaryFilters = newSecondaryFilterParams[0];
    newSearchQuery.sortBy = newSecondaryFilterParams[1];
    
    this.setState({ 
      searchQuery: newSearchQuery,
      activeSecondaryFilters: newSecondaryFilters,
    }, () => {
      addQuery(newSearchQuery);
      this.getSearchResults({ pageNum: newSearchQuery.pageNum, secondaryFilters: this.state.activeSecondaryFilters });
    });
  };

  handleClearSecondaryFiltersClick = () => {
    const newSearchQuery = { ...this.state.searchQuery };
    newSearchQuery.pageNum = 1;
    
    this.setState({ 
      activeSecondaryFilters: {},
    }, () => {
      addQuery(newSearchQuery);
      this.getSearchResults({ pageNum: newSearchQuery.pageNum, activeSecondaryFilters: this.state.activeSecondaryFilters });
    });
  };

  render() {
    const { search } = this.props;
    const { searchQuery, filterDropdownOpen, loadingSearchResults, activeSecondaryFilters } = this.state;
    const { primaryFilter } = searchQuery;
    const filteredResults = search.result_count.filter(filter => filter.itemCount > 0);
    const filteredSecondaryFilters = search.secondaryFilters[searchQuery.primaryFilter];

    return (
      <PageContainer classes="search-page bg-white">
        <SearchPrimaryFilters
          activeFilter={primaryFilter}
          searchResults={search}
          onClick={this.handlePrimaryFilterClick}
        />

        <MainContentContainer>
          <MaxWidthContainer>
            <SearchHeading searchQuery={searchQuery} searchResults={search} />

            {search.original_query !== undefined &&
              <SpellCheck
                originalQuery={search.original_query}
                correctedQuery={search.corrected_query}
                spellcheckEnabled={search.spellcheck_enabled}
                onEnableSpellcheckClick={this.handleEnableSpellcheckClick}
                onOriginalQuerySearchClick={this.handleOriginalQuerySearchClick}
              />
            }
          </MaxWidthContainer>

          <div className="search-results-new">
            {primaryFilter === `all` &&
              <MaxWidthContainer>
                {loadingSearchResults === true || search.results.length === 0 ? (
                  <NoResultsMessage
                    title="No results found"
                    // text="Click the button below to send a request to our content team to let us know the kind of content you want."
                    // cta={<Button onClick={this.handleRequestContentClick}>Request content</Button>}
                    loading={loadingSearchResults}
                    border
                  />
                ) : (
                  <div className="row">
                    <div className="col-lg-8">
                      {filteredResults
                        .filter(filter => filter.itemName !== `user` && filter.itemName !== `club` && filter.itemName !== `team`)
                        .map(filter => this.buildSearchResults(filter, 3, `col-md-4`))
                      }
                    </div>

                    <div className="col-lg-4">
                      <div className="search-page__right-sidebar">
                        {filteredResults
                            .filter(filter => filter.itemName === `user`)
                            .map(filter => this.buildSearchResults(filter, 3, `col-sm-6 col-md-12`))
                        }

                        {filteredResults
                            .filter(filter => filter.itemName === `club`)
                            .map(filter => this.buildSearchResults(filter, 3, `col-sm-6 col-md-12`))
                        }

                        {filteredResults
                            .filter(filter => filter.itemName === `team`)
                            .map(filter => this.buildSearchResults(filter, 3, `col-sm-6 col-md-12`))
                        }
                      </div>
                    </div>
                  </div>
                )}
              </MaxWidthContainer>
            }

            {primaryFilter !== `all` &&
              <div className="search-results-new__specific-type">
                {/* If the current primary filters support secondary filters */}
                {filteredSecondaryFilters &&
                  <SearchSecondaryFilters
                    filters={filteredSecondaryFilters}
                    filterDropdownOpen={filterDropdownOpen}
                    onDropdownClick={this.handleDropdownClick}
                    onFilterChange={this.handleSecondaryFilterChange}
                    activeFilters={activeSecondaryFilters}
                    onClearClick={this.handleClearSecondaryFiltersClick}
                  />
                }

                <div className="p-t-5 border-top-1 border-color-grey--light">
                  <MaxWidthContainer>
                    {loadingSearchResults === true || search.results.length === 0 ? (
                      <NoResultsMessage
                        title="No results found"
                        // text="Click the button below to send a request to our content team to let us know the kind of content you want."
                        // cta={<Button onClick={this.handleRequestContentClick}>Request content</Button>}
                        loading={loadingSearchResults}
                        border
                      />
                    ) : (
                      <SearchList filterName={primaryFilter} searchResults={this.props.search.results} listItemClasses="col-md-4 col-lg-3 m-b-6" />
                    )}

                    {search.links && search.results.length > 0 &&
                      <PageNumberInfo
                        currentPage={searchQuery.pageNum}
                        totalPages={this.getPageNumFromLink(search.links.last)}
                        loading={loadingSearchResults}
                      />
                    }

                    {search.links &&
                      <Pager
                        loading={loadingSearchResults}
                        showPrevButton={`prev` in search.links}
                        showNextButton={`next` in search.links}
                        onPrevClick={this.handlePrevClick}
                        onNextClick={this.handleNextClick}
                      />
                    }
                  </MaxWidthContainer>
                </div>
              </div>
            }
          </div>
        </MainContentContainer>
      </PageContainer>
    );
  }
}

SearchPage.propTypes = {
  search: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    loggedInUser: state.loggedInUser,
    search: state.search,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({}, searchActions), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchPage);



// WEBPACK FOOTER //
// ./library/app/components/Search/SearchPage.js